/**
 * PlanControlBar - Controls for AI plan documents
 * 
 * Provides plan-specific controls:
 * - Status badge (draft/ready/executing/paused/completed/failed)
 * - Link target document
 * - Mark as Ready
 * - Execute/Resume/Retry/Abort
 * - Edit mode toggle
 */

import { useState } from 'react';
import { 
  useToast, 
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui';
import { Loader2 } from 'lucide-react';
import { logger } from '../../utils/logger';

type PlanStatus = 'draft' | 'ready' | 'executing' | 'paused' | 'completed' | 'failed';

interface PlanControlBarProps {
  documentNumber: string;
  projectId: string;
  planStatus: PlanStatus;
  targetDocument: { id: string; name: string; version?: string } | null;
  taskCount: number;
  completedTaskCount: number;
  onLinkDocument: () => void;
  onUnlinkDocument: () => void;
  onMarkReady: () => void;
  onRevokeReady: () => void;
  onExecute: () => void;
  onPause: () => void;
  onResume: () => void;
  onRetry: () => void;
  onAbort: () => void;
  onEnableEdit?: () => void;
  canEdit: boolean;
  isProcessing?: boolean;
}

const statusConfig: Record<PlanStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-muted text-muted-foreground' },
  ready: { label: 'Ready', className: 'bg-info-light text-info' },
  executing: { label: 'Executing', className: 'bg-warning-light text-warning' },
  paused: { label: 'Paused', className: 'bg-secondary text-secondary-foreground' },
  completed: { label: 'Completed', className: 'bg-success-light text-success' },
  failed: { label: 'Failed', className: 'bg-destructive-light text-destructive' },
};

export default function PlanControlBar({
  documentNumber: _documentNumber,
  projectId: _projectId,
  planStatus,
  targetDocument,
  taskCount,
  completedTaskCount,
  onLinkDocument,
  onUnlinkDocument,
  onMarkReady,
  onRevokeReady,
  onExecute,
  onPause: _onPause,
  onResume,
  onRetry,
  onAbort,
  onEnableEdit,
  canEdit,
  isProcessing = false,
}: PlanControlBarProps) {
  // Unused props marked with underscore - kept for API compatibility
  void _documentNumber;
  void _projectId;
  void _onPause;
  
  logger.debug('[PlanControlBar] Rendering with:', { planStatus, targetDocument, taskCount, canEdit });
  const [showAbortModal, setShowAbortModal] = useState(false);
  const { error } = useToast();

  // Validation: Can mark as ready?
  const canMarkReady = planStatus === 'draft' && targetDocument !== null && taskCount > 0;

  // Status-based button visibility
  const showExecuteButton = planStatus === 'ready';
  const showResumeButton = planStatus === 'paused';
  const showRetryButton = planStatus === 'failed';
  const showAbortButton = planStatus === 'executing';
  // Note: Pause button shares state with Abort button (showAbortButton)
  const showMarkReadyButton = planStatus === 'draft';
  const showLinkButton = planStatus === 'draft' && targetDocument === null;
  // Edit requires a linked target document - can't create/edit tasks without knowing the target
  const showEditButton = canEdit && onEnableEdit && targetDocument !== null && (planStatus === 'draft' || planStatus === 'completed' || planStatus === 'failed' || planStatus === 'ready');
  const showEditBlockedMessage = canEdit && onEnableEdit && targetDocument === null && planStatus === 'draft';

  const handleAbortConfirm = () => {
    setShowAbortModal(false);
    onAbort();
  };

  const status = statusConfig[planStatus];

  return (
    <>
      {/* Control Bar */}
      <div className="flex items-center gap-2 flex-shrink-0 bg-muted border-b border-border px-4 py-2">
        {/* Status Badge */}
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${status.className}`}>
          {status.label}
        </span>

        {/* Target Document Info */}
        {targetDocument && (
          <span 
            className="text-xs bg-info-light text-info px-2 py-0.5 rounded inline-flex items-center gap-1"
            title={targetDocument.name}
          >
            📄 Target: {targetDocument.id}
            {planStatus === 'draft' && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onUnlinkDocument();
                }}
                disabled={isProcessing}
                variant="ghost"
                size="xs"
                className="ml-1 h-4 w-4 p-0 hover:text-destructive"
                title="Remove linked document"
              >
                ✕
              </Button>
            )}
          </span>
        )}

        {/* Progress during execution */}
        {planStatus === 'executing' && taskCount > 0 && (
          <span className="text-xs text-muted-foreground ml-2">
            {completedTaskCount} / {taskCount} tasks
          </span>
        )}

        <div className="flex-1" />

        {/* Link Target Document Button */}
        {showLinkButton && (
          <Button
            onClick={onLinkDocument}
            disabled={isProcessing}
            variant="secondary"
            size="xs"
            title="Link this plan to a target document"
          >
            🔗 Link Target Document
          </Button>
        )}

        {/* Mark as Ready Button */}
        {showMarkReadyButton && (
          <Button
            onClick={() => {
              if (!targetDocument) {
                error('Cannot Mark Ready', 'Please link a target document first');
                return;
              }
              if (taskCount === 0) {
                error('Cannot Mark Ready', 'Plan has no tasks defined. Use AI to generate tasks.');
                return;
              }
              onMarkReady();
            }}
            disabled={isProcessing || !canMarkReady}
            variant="secondary"
            size="xs"
            title={
              !targetDocument 
                ? 'Link a target document first' 
                : taskCount === 0 
                  ? 'Plan needs tasks - use AI to generate them'
                  : 'Mark plan as ready for execution'
            }
          >
            ✓ Mark Ready {taskCount === 0 && '(needs tasks)'}
          </Button>
        )}

        {/* Revoke Ready Button (when ready, allows going back to draft) */}
        {showExecuteButton && (
          <Button
            onClick={onRevokeReady}
            disabled={isProcessing}
            variant="outline"
            size="xs"
            title="Return plan to draft status for editing"
          >
            ↩ Back to Draft
          </Button>
        )}

        {/* Execute Button */}
        {showExecuteButton && (
          <Button
            onClick={onExecute}
            disabled={isProcessing}
            variant="success"
            size="xs"
          >
            ▶️ Execute
          </Button>
        )}

        {/* Resume Button (when paused) */}
        {showResumeButton && (
          <Button
            onClick={onResume}
            disabled={isProcessing}
            variant="success"
            size="xs"
          >
            ▶️ Resume
          </Button>
        )}

        {/* Retry Button (when failed) */}
        {showRetryButton && (
          <Button
            onClick={onRetry}
            disabled={isProcessing}
            variant="warning"
            size="xs"
          >
            🔄 Retry
          </Button>
        )}

        {/* Executing State: Spinner + Abort */}
        {showAbortButton && (
          <>
            <Button
              disabled
              variant="secondary"
              size="xs"
            >
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
              Executing...
            </Button>
            <Button
              onClick={() => setShowAbortModal(true)}
              disabled={isProcessing}
              variant="outline"
              size="xs"
            >
              ⏹️ Abort
            </Button>
          </>
        )}

        {/* Edit Button */}
        {showEditButton && (
          <Button
            onClick={onEnableEdit}
            data-edit-button="true"
            size="xs"
          >
            ✏️ Edit
          </Button>
        )}

        {/* Edit blocked - need to link document first */}
        {showEditBlockedMessage && (
          <span className="text-xs text-muted-foreground italic">
            Link a target document to enable editing
          </span>
        )}
      </div>

      {/* Abort Confirmation Modal */}
      <Dialog open={showAbortModal} onOpenChange={setShowAbortModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Abort Plan Execution?</DialogTitle>
            <DialogDescription>
              This will pause the plan execution. You can resume or edit the plan after aborting.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-warning-light rounded-md p-3 border-l-4 border-warning">
              <p className="text-sm text-warning">
                <strong>Note:</strong> Completed tasks will remain completed. 
                <br />
                <span className="text-xs">You can edit the plan and resume execution later.</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => setShowAbortModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAbortConfirm}
              disabled={isProcessing}
              variant="warning"
            >
              Abort Execution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
